import styled from 'styled-components';
import Modal from '../../Modal/Modal';
import { H2, PSmall } from '../../elements/Text';
import { CloseIcon } from '../../Icons';
import { useGetCompany } from '../../../hooks/use-hydra/admin';
import { FormLabel } from '../../Form';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { Dropdown } from '../../elements/Dropdown';
import Button from '../../elements/Button';
import { toast } from 'react-toastify';
import { CompanyResponse, inviteLearnersToCohort } from '../../../api/admin';
import { ScrollableTooltip } from '../../Tooltip';
import { useMutation } from 'react-query';
import useAuth from '../../../hooks/use-auth';
import { ROUTES } from '../../../routing';

const ErrorText = styled(PSmall)`
  color: ${({ theme }) => theme.colors.textDanger};
`;

const ErrorLink = styled(Link)`
  color: ${({ theme }) => theme.colors.textDanger};
  font-style: normal;
  font-weight: 400;
  font-size: 15px;
  line-height: 20px;
  /* identical to box height, or 133% */
  font-feature-settings: 'tnum' on, 'lnum' on;
  transition: all 300ms ease-in-out;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const Wrapper = styled(Modal)`
  padding-bottom: 72px;
  padding-top: 64px;
  width: 80vw;
  min-width: 400px;
  overflow-y: auto;
  background-color: ${({ theme }) => theme.colors.bgPage2};
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
`;

const CloseButton = styled(CloseIcon)`
  padding: 0;
`;

const Header = styled(H2)`
  margin: 0;
  padding: 0;
`;
const DateText = styled(PSmall)`
  color: ${({ theme }) => theme.colors.textPrimary};
`;
type LearnerSplitType = {
  learnersInCohort: CompanyResponse['learners'][number][];
  learnersWithoutLearningPlan: CompanyResponse['learners'][number][];
  learnersToInvite: CompanyResponse['learners'][number][];
};

const getInviteButtonState = ({
  nextOpenCohort,
  learnersInCohort,
  learnersWithoutLearningPlan,
  company,
}: {
  nextOpenCohort: CompanyResponse['cohorts'][number] | undefined;
  learnersInCohort: CompanyResponse['learners'][number][];
  learnersWithoutLearningPlan: CompanyResponse['learners'][number][];
  company: CompanyResponse | null | undefined;
}) => {
  if (!nextOpenCohort) {
    return {
      canInvite: false,
      tooltip: 'There are no open cohorts. Create one first',
    };
  }

  if (!nextOpenCohort.officeHourTimes?.length) {
    return {
      canInvite: false,
      tooltip: 'This cohort has no kick off times or office hours configured',
    };
  }

  if (company?.settings.KICK_OFF && !nextOpenCohort.kickOffTimes?.length) {
    return {
      canInvite: false,
      tooltip: 'This cohort has no kick off times configured',
    };
  }

  if (learnersWithoutLearningPlan.length > 0) {
    return {
      canInvite: false,
      tooltip:
        'There are learners without a learning plan. Please add a learning plan first.',
    };
  }

  if (learnersInCohort.length > 0) {
    return {
      canInvite: false,
      tooltip:
        'There are learners with invites or progress in the selected cohort.',
    };
  }

  return {
    canInvite: true,
    tooltip: undefined,
  };
};

export default function InviteLearnersToCohortModal({
  isOpen,
  onClose,
  companyId,
  selectedLearnerIds,
}: {
  isOpen: boolean;
  onClose: () => void;
  companyId: number;
  selectedLearnerIds: number[];
}) {
  const {
    data,
    refetch: refetchCompany,
    isLoading: isLoadingCompany,
  } = useGetCompany(companyId);

  const nextOpenCohort = data?.cohorts.find((c) => c.isOpen);

  const { getTokenSilently } = useAuth();

  const { learnersInCohort, learnersWithoutLearningPlan, learnersToInvite } =
    selectedLearnerIds.reduce<LearnerSplitType>(
      (acc, learnerId) => {
        const learner = data?.learners.find((l) => l.id === learnerId);
        if (!learner) {
          throw new Error(`Learner with id ${learnerId} not found`);
        }

        // Check if learner doesn't have a learning plan
        if (!learner.learningPlan || learner.learningPlan.length === 0) {
          acc.learnersWithoutLearningPlan.push(learner);
          return acc;
        }

        // Check if learner is already in the current open cohort
        const isLearnerInCohort = nextOpenCohort?.learners.find(
          (cohortLearner) => cohortLearner?.learnerId === learnerId
        );

        if (isLearnerInCohort) {
          acc.learnersInCohort.push(learner);
          return acc;
        }

        // Learner can be invited
        acc.learnersToInvite.push(learner);
        return acc;
      },
      {
        learnersInCohort: [],
        learnersWithoutLearningPlan: [],
        learnersToInvite: [],
      } as LearnerSplitType
    );

  const startDateFromNow = dayjs.utc(nextOpenCohort?.startDate).fromNow();
  const isStartDateInThePast = dayjs
    .utc(nextOpenCohort?.startDate)
    .isBefore(dayjs.utc());
  const nextOpenCohortOfficeHours = nextOpenCohort?.officeHourTimes ?? [];
  const nextOpenCohortKickOffTimes = nextOpenCohort?.kickOffTimes ?? [];

  const { canInvite, tooltip: buttonDisableTooltip } = getInviteButtonState({
    nextOpenCohort,
    learnersInCohort,
    learnersWithoutLearningPlan,
    company: data,
  });

  const cohortValue = nextOpenCohort
    ? {
        label: `${dayjs
          .utc(nextOpenCohort.startDate)
          .format('MMM D, YYYY')} - ${dayjs
          .utc(nextOpenCohort.endDate)
          .format('MMM D, YYYY')}`,
        value: nextOpenCohort.cycleId,
      }
    : null;

  const inviteLearnersMutation = useMutation({
    mutationFn: async () => {
      if (!nextOpenCohort?.cycleId) {
        throw new Error('There is no open cohort. Create one first.');
      }

      if (selectedLearnerIds.length === 0) {
        throw new Error('Please select at least one learner.');
      }

      const token = await getTokenSilently();
      return inviteLearnersToCohort(token, {
        companyId,
        cohortId: nextOpenCohort?.cycleId,
        learnerIds: selectedLearnerIds,
      });
    },
    onSuccess: async () => {
      await refetchCompany();
      toast.success('Learners invited successfully');
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof Error ? error.message : 'Failed to invite learners'
      );
    },
  });

  if (!isOpen) {
    return null;
  }

  return (
    <Wrapper>
      <Row>
        <Header>Invite Learners to Cohort</Header>
        <CloseButton onClick={onClose} />
      </Row>
      <FormLabel>Next Open Cohort</FormLabel>
      {nextOpenCohort ? (
        <Dropdown
          options={[cohortValue]}
          value={cohortValue}
          isDisabled
          menuPortalTarget={document.body}
        />
      ) : (
        <ErrorText>
          No open cohort found. Create one{' '}
          <ErrorLink
            to={ROUTES.ADMIN.COHORTS.getPath({
              companyId: companyId.toString(),
            })}
            target="_blank"
          >
            here
          </ErrorLink>
          .
        </ErrorText>
      )}

      <FormLabel>Office Hours</FormLabel>
      {nextOpenCohortOfficeHours?.length > 0 ? (
        <DateText>
          {nextOpenCohortOfficeHours
            .map((o) => dayjs.utc(o).format('dddd, MMMM D [at] HH:mm [UTC]'))
            .join(', ')}
        </DateText>
      ) : (
        <ErrorText>No office hours configured for this cohort.</ErrorText>
      )}

      {data?.settings.KICK_OFF && (
        <>
          <FormLabel>Kick Off Times</FormLabel>
          {nextOpenCohortKickOffTimes?.length > 0 ? (
            <DateText>
              {nextOpenCohortKickOffTimes
                .map((o) =>
                  dayjs.utc(o).format('dddd, MMMM D [at] HH:mm [UTC]')
                )
                .join(', ')}
            </DateText>
          ) : (
            <ErrorText>No kick off times configured for this cohort.</ErrorText>
          )}
        </>
      )}

      <PSmall style={{ marginTop: '32px' }}>
        You are about to invite {selectedLearnerIds.length} learners to the
        cohort.
      </PSmall>

      {learnersInCohort.length > 0 && (
        <>
          <PSmall>
            There are{' '}
            <Bold className="learners-in-cohort">
              {learnersInCohort.length} learners
            </Bold>{' '}
            with progress or invites in the selected cohort. You can't invite
            invite them again.
          </PSmall>
          <EmailsTooltip
            emails={learnersInCohort.map((l) => `${l.name} (${l.email})`)}
            buttonId={'learners-in-cohort'}
          />
        </>
      )}
      {learnersWithoutLearningPlan.length > 0 && (
        <>
          <PSmall>
            There are{' '}
            <Bold className="learners-without-learning-plan">
              {learnersWithoutLearningPlan.length} learners
            </Bold>{' '}
            without a learning plan. Please update their learning plan first.
          </PSmall>
          <EmailsTooltip
            emails={learnersWithoutLearningPlan.map(
              (l) => `${l.name} (${l.email})`
            )}
            buttonId={'learners-without-learning-plan'}
          />
        </>
      )}

      {learnersToInvite.length > 0 && (
        <>
          <PSmall>
            There are{' '}
            <Bold className="learners-to-invite">
              {learnersToInvite.length} learners
            </Bold>{' '}
            that will be invited to the cohort. The next cohort{' '}
            {isStartDateInThePast ? 'started' : 'starts'} {startDateFromNow}.
          </PSmall>
          <EmailsTooltip
            emails={learnersToInvite.map((l) => `${l.name} (${l.email})`)}
            buttonId={'learners-to-invite'}
          />
        </>
      )}
      <Row>
        <Button
          label="Invite Learners"
          showIconBeforeLabel
          faIcon="fa-solid fa-paper-plane"
          type="primary"
          onClick={() => {
            inviteLearnersMutation.mutate();
          }}
          isDisabled={!canInvite || inviteLearnersMutation.isLoading}
          isLoading={inviteLearnersMutation.isLoading || isLoadingCompany}
          tooltip={buttonDisableTooltip}
        />
      </Row>
    </Wrapper>
  );
}

const EmailsTooltip = ({
  emails,
  buttonId,
}: {
  emails: string[];
  buttonId: string;
}) => {
  return (
    <ScrollableTooltip
      anchorSelect={`.${buttonId}`}
      place="top"
      delayHide={500}
    >
      <div style={{ overflowY: 'auto' }}>
        <span
          style={{
            whiteSpace: 'pre-wrap',
            overflowY: 'auto',
          }}
        >
          {emails.join('\n')}
        </span>
      </div>
    </ScrollableTooltip>
  );
};

const Bold = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;
`;
